import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

/**
 * Button variants.
 *
 *   default            filled cyan — the one primary action per screen.
 *   secondary          muted fill for the second action in a group.
 *   outline            neutral bordered action.
 *   ghost              bare text action (toolbars, menus).
 *   link               inline text link.
 *   destructive        outline/text coral — the default for dangerous
 *                      actions outside a confirmation (white on coral fill
 *                      is only 2.8:1, so the fill is not used here).
 *   destructive-solid  coral fill with dark text (4.5:1+) — dialog confirm.
 *   cyber / cyber-cyan bordered cyan; cyber is the heavier of the two.
 *   cyber-magenta      bordered magenta — take-off-air / delete confirm.
 *   cyber-amber        bordered amber — discard.
 *
 * No glow box-shadows anywhere; state is carried by colour and border.
 * Disabled buttons keep 60% opacity plus explicit muted colours so the
 * label stays legible.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-cyber-cyan text-background hover:bg-cyber-cyan/90 disabled:bg-muted disabled:text-foreground',
        destructive:
          'border border-destructive/50 bg-transparent text-destructive hover:bg-destructive/10 disabled:border-border disabled:text-muted-foreground',
        'destructive-solid':
          'bg-destructive text-background hover:bg-destructive/90 disabled:bg-muted disabled:text-foreground',
        outline:
          'border border-border bg-transparent text-foreground hover:bg-muted/60 disabled:text-muted-foreground',
        secondary:
          'bg-muted text-foreground hover:bg-muted/80 disabled:text-muted-foreground',
        ghost:
          'text-foreground hover:bg-muted/60 disabled:text-muted-foreground',
        link: 'text-cyber-cyan underline-offset-4 hover:underline',
        cyber:
          'border border-cyber-cyan/60 bg-transparent text-cyber-cyan hover:bg-cyber-cyan/15 focus-visible:ring-cyber-cyan disabled:border-border disabled:text-muted-foreground',
        'cyber-cyan':
          'border border-cyber-cyan/50 bg-transparent text-cyber-cyan hover:bg-cyber-cyan/10 disabled:border-border disabled:text-muted-foreground',
        'cyber-magenta':
          'border border-cyber-magenta/50 bg-transparent text-cyber-magenta hover:bg-cyber-magenta/10 disabled:border-border disabled:text-muted-foreground',
        'cyber-amber':
          'border border-cyber-amber/50 bg-transparent text-cyber-amber hover:bg-cyber-amber/10 disabled:border-border disabled:text-muted-foreground',
      },
      size: {
        default: 'h-10 px-6 py-2 pointer-coarse:min-h-11',
        sm: 'h-9 rounded-lg px-4 pointer-coarse:min-h-11',
        lg: 'h-11 rounded-lg px-8',
        icon: 'h-10 w-10 pointer-coarse:min-h-11 pointer-coarse:min-w-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
